/**
 * Segmented tab strip: a recessed track holding one raised pill per view.
 *
 * The selected tab is the one that LOOKS like a surface -- it is lifted out of
 * the track with the page's own background and a small shadow, while the rest
 * stay flush with it. That is the whole affordance, so it does not need a
 * saturated fill to carry the selected state, and the strip stays quiet enough
 * to sit above a toolbar without competing with the actions in it.
 *
 * Extracted from the inline strip in RoleManagement so the two agree. Icons are
 * optional: a tab set whose labels already read clearly (findings by
 * compliance state) is better without them.
 */

import type { LucideIcon } from "lucide-react";
import { cn } from "./ui/utils";

export interface SegmentedTabItem {
  id: string;
  label: string;
  /** Trailing count badge. Omit for a tab that has nothing to total. */
  count?: number;
  /** Optional leading glyph. Left out entirely when not supplied. */
  icon?: LucideIcon;
}

export interface SegmentedTabsProps {
  items: SegmentedTabItem[];
  /** Id of the active tab. */
  value: string;
  onChange: (id: string) => void;
  /** Accessible name for the tablist. */
  ariaLabel?: string;
  className?: string;
  /** "sm" tightens padding and text for toolbar-sized strips. */
  size?: "default" | "sm";
  /**
   * Drop each tab's label once the nearest `@container` ancestor (e.g. a
   * ChartCard title row) is too narrow to hold the title and the full strip
   * side by side, leaving just the icon -- so a long chart title never forces
   * the strip onto its own row. Requires every item to carry an `icon`.
   */
  responsiveLabels?: boolean;
}

export function SegmentedTabs({
  items,
  value,
  onChange,
  ariaLabel = "Views",
  className,
  size = "default",
  responsiveLabels = false,
}: SegmentedTabsProps) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "flex w-fit max-w-full flex-wrap items-center gap-1 rounded-lg bg-muted/50 p-1 dark:bg-white/[0.03]",
        className,
      )}
    >
      {items.map((item) => {
        const active = item.id === value;
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={responsiveLabels ? item.label : undefined}
            title={responsiveLabels ? item.label : undefined}
            onClick={() => onChange(item.id)}
            className={cn(
              "flex items-center gap-2 rounded-md font-medium transition-all",
              size === "sm" ? "px-3 py-1 text-xs" : "px-4 py-2 text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-indigo-600 text-white shadow-sm"
                : "text-muted-foreground hover:bg-background/50 hover:text-foreground dark:hover:bg-white/[0.04]",
            )}
          >
            {Icon ? <Icon className="size-4 shrink-0" aria-hidden /> : null}
            <span className={cn("whitespace-nowrap", responsiveLabels && "hidden @2xl:inline")}>
              {item.label}
            </span>
            {item.count !== undefined && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums",
                  // On the raised pill the badge needs a tint one step further
                  // from the surface, or it disappears into it.
                  active ? "bg-muted text-foreground" : "bg-background/60 text-muted-foreground",
                )}
              >
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
